import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Printer, Send } from 'lucide-react';
import T from '../T';
import { useTranslate } from '../../hooks/useTranslate';
import { useSiteContent } from '../../hooks/useSiteContent';
import { useLocalizedText } from '../../hooks/useLocalizedText';

const FUNCTION_URL = 'https://us-central1-yliulaw-37283.cloudfunctions.net/sendIntakeEmail';

const MATTER_TYPES = [
  'Civil Litigation',
  'Employment',
  'Family',
  'Contract Dispute',
  'Real Estate',
  'Business/Commercial',
  'Franchise',
  'Personal Injury',
  'Consumer Protection',
  'Other',
] as const;

type PropertyCountOption = '1' | '2' | '3' | 'more';

type PropertyEntry = {
  address: string;
  yearPurchased: string;
  currentFmv: string;
  mortgage: string;
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - 1899 },
  (_, idx) => String(CURRENT_YEAR - idx),
);
const MARRIED_YEAR_OPTIONS = Array.from({ length: 100 }, (_, idx) => String(idx + 1));

const createPropertyEntry = (): PropertyEntry => ({
  address: '',
  yearPurchased: String(CURRENT_YEAR),
  currentFmv: '0',
  mortgage: '0',
});

const getPropertyCount = (option: PropertyCountOption): number => {
  if (option === 'more') return 0;
  return Number(option);
};

const resizePropertyEntries = (existing: PropertyEntry[], targetCount: number): PropertyEntry[] => {
  if (targetCount <= 0) return [];
  if (existing.length === targetCount) return existing;
  if (existing.length > targetCount) return existing.slice(0, targetCount);
  return [...existing, ...Array.from({ length: targetCount - existing.length }, () => createPropertyEntry())];
};

const getTodayIso = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

const IntakePage: React.FC = () => {
  const { content } = useSiteContent();
  const [fullLegalName, setFullLegalName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [briefDescription, setBriefDescription] = useState('');

  const [matterTypes, setMatterTypes] = useState<string[]>([]);
  const [civilOtherParties, setCivilOtherParties] = useState<string[]>(['']);
  const [familyParties, setFamilyParties] = useState<string[]>(['']);

  const [realEstatePropertyCount, setRealEstatePropertyCount] = useState<PropertyCountOption>('1');
  const [realEstateProperties, setRealEstateProperties] = useState<PropertyEntry[]>([createPropertyEntry()]);
  const [realEstatePropertiesMore, setRealEstatePropertiesMore] = useState('');

  const [agentContact, setAgentContact] = useState('');
  const [closingDate, setClosingDate] = useState(getTodayIso);

  const [familyChildrenCount, setFamilyChildrenCount] = useState<number>(0);
  const [familyMarriageYear, setFamilyMarriageYear] = useState<string>(String(CURRENT_YEAR));
  const [familyMarriedYears, setFamilyMarriedYears] = useState<string>('1');
  const [familyPropertyCount, setFamilyPropertyCount] = useState<PropertyCountOption>('1');
  const [familyProperties, setFamilyProperties] = useState<PropertyEntry[]>([createPropertyEntry()]);
  const [familyPropertiesMore, setFamilyPropertiesMore] = useState('');

  const [dateValue, setDateValue] = useState(getTodayIso);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [singleScreenMode, setSingleScreenMode] = useState(false);

  const briefDescriptionPlaceholder = useTranslate('Please provide key facts and context relevant to your matter.');
  const otherPartyPrefix = useTranslate('Other party');
  const partyOccupationPrefix = useTranslate('Party and occupation');
  const propertyAddressLabel = useTranslate('Property Address');
  const listAllPropertyAddresses = useTranslate('List all property addresses and details.');
  const listAllFamilyPropertyAddresses = useTranslate('List all family property addresses and details.');
  const yearLabel = useTranslate('Year');
  const yearsLabel = useTranslate('Years');
  const firmName = useLocalizedText(content.contact.firmName);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 430px), (max-height: 760px)');

    const updateMode = () => {
      setSingleScreenMode(mediaQuery.matches);
    };

    updateMode();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateMode);
      return () => mediaQuery.removeEventListener('change', updateMode);
    }

    mediaQuery.addListener(updateMode);
    return () => mediaQuery.removeListener(updateMode);
  }, []);

  const hasCivilLitigation = matterTypes.includes('Civil Litigation');
  const hasFamily = matterTypes.includes('Family');
  const hasRealEstate = matterTypes.includes('Real Estate');

  const addCivilPartyField = () => {
    setCivilOtherParties((prev) => [...prev, '']);
  };

  const addFamilyPartyField = () => {
    setFamilyParties((prev) => [...prev, '']);
  };

  const handlePropertyCountChange = (
    value: string,
    setCount: React.Dispatch<React.SetStateAction<PropertyCountOption>>,
    setProperties: React.Dispatch<React.SetStateAction<PropertyEntry[]>>,
  ) => {
    const option = value as PropertyCountOption;
    setCount(option);
    const targetCount = getPropertyCount(option);
    if (targetCount > 0) {
      setProperties((prev) => resizePropertyEntries(prev, targetCount));
    }
  };

  const updateCivilParty = (idx: number, value: string) => {
    setCivilOtherParties((prev) => prev.map((item, i) => (i === idx ? value : item)));
  };

  const updateFamilyParty = (idx: number, value: string) => {
    setFamilyParties((prev) => prev.map((item, i) => (i === idx ? value : item)));
  };

  const updatePropertyField = (
    idx: number,
    field: keyof PropertyEntry,
    value: string,
    setProperties: React.Dispatch<React.SetStateAction<PropertyEntry[]>>,
  ) => {
    setProperties((prev) => prev.map((item, i) => (i === idx ? {...item, [field]: value} : item)));
  };

  const handleMatterTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValues = Array.from(event.target.selectedOptions).map((option) => option.value);
    setMatterTypes(selectedValues);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullLegalName,
          address,
          email,
          phone,
          briefDescription,
          matterTypes,
          civilOtherParties,
          familyParties,
          realEstatePropertyCount,
          realEstateProperties,
          realEstatePropertiesMore,
          agentContact,
          closingDate,
          familyChildrenCount,
          familyMarriageYear,
          familyMarriedYears,
          familyPropertyCount,
          familyProperties,
          familyPropertiesMore,
          intakeDateIso: dateValue,
          intakeDateDisplay: dateValue,
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to send. Please call (905) 604-5521');
      }

      setStatus('sent');
    } catch (error: unknown) {
      setStatus('error');
      setErrorMsg(
        error instanceof Error && error.message
          ? error.message
          : 'Unable to send. Please call (905) 604-5521',
      );
    }
  };

  return (
    <div className={`intake-page-wrap${singleScreenMode ? ' intake-single-screen' : ''}`}>
      <div className="container intake-sheet">
        <section className="intake-top-date">
          <label htmlFor="intake-date">
            <strong><T>Date</T></strong>
          </label>
          <input
            id="intake-date"
            type="date"
            value={dateValue}
            onChange={(event) => setDateValue(event.target.value)}
          />
        </section>

        <section className="intake-header">
          <div className="intake-logo-row">
            <div>
              <h1><T>Client Intake Form</T></h1>
              <p><T>Complete this form to help us prepare your file efficiently.</T></p>
            </div>
          </div>
          <div className="intake-contact-block">
            <h2>{firmName}</h2>
            {content.contact.torontoOffice.addressLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
            <p>Phone: {content.contact.torontoOffice.phone}</p>
            <p>Fax: {content.contact.torontoOffice.fax}</p>
            <p>Email: {content.contact.torontoOffice.email}</p>
          </div>
        </section>

        <section className="intake-form-card">
          <form onSubmit={handleSubmit}>
          <fieldset className="intake-section intake-client-info">
            <legend><T>Client Information</T></legend>

            <div className="intake-grid two-col">
              <div>
                <label htmlFor="full-legal-name"><T>Full Legal Name</T></label>
                <input
                  id="full-legal-name"
                  type="text"
                  required
                  value={fullLegalName}
                  onChange={(event) => setFullLegalName(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="client-email"><T>Email</T></label>
                <input
                  id="client-email"
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
            </div>

            <div className="intake-grid two-col">
              <div>
                <label htmlFor="client-address"><T>Address</T></label>
                <input
                  id="client-address"
                  type="text"
                  required
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="client-phone"><T>Phone</T></label>
                <input
                  id="client-phone"
                  type="tel"
                  required
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="brief-description"><T>Brief Description</T></label>
              <textarea
                id="brief-description"
                rows={3}
                maxLength={2000}
                value={briefDescription}
                onChange={(event) => setBriefDescription(event.target.value)}
                placeholder={briefDescriptionPlaceholder}
                required
              />
              <p className="intake-character-count">{briefDescription.length}/2000</p>
            </div>
          </fieldset>

          <fieldset className="intake-section intake-legal-matter">
            <legend><T>Legal Matter</T></legend>

            <div>
              <label htmlFor="matter-types"><T>Type (multiple select)</T></label>
              <select
                id="matter-types"
                multiple
                value={matterTypes}
                onChange={handleMatterTypeChange}
                className="intake-matter-select"
              >
                {MATTER_TYPES.map((matter) => (
                  <option key={matter} value={matter}><T>{matter}</T></option>
                ))}
              </select>
              <p className="intake-help-text"><T>Hold Ctrl (Windows) or Command (Mac) to choose more than one type.</T></p>
            </div>

            {hasCivilLitigation && (
              <div className="intake-conditional-block">
                <h3><T>Full Name(s) of the Other Parties Involved</T></h3>
                {civilOtherParties.map((party, idx) => (
                  <input
                    key={`civil-party-${idx}`}
                    type="text"
                    value={party}
                    onChange={(event) => updateCivilParty(idx, event.target.value)}
                    placeholder={`${otherPartyPrefix} ${idx + 1}`}
                  />
                ))}
                <button type="button" className="btn btn-secondary" onClick={addCivilPartyField}>
                  <T>Add Another Party</T>
                </button>
              </div>
            )}

            {hasRealEstate && (
              <div className="intake-conditional-block">
                <h3><T>Real Estate Details</T></h3>
                <label htmlFor="real-estate-property-count"><T>Number of Properties</T></label>
                <select
                  id="real-estate-property-count"
                  value={realEstatePropertyCount}
                  onChange={(event) => handlePropertyCountChange(
                    event.target.value,
                    setRealEstatePropertyCount,
                    setRealEstateProperties,
                  )}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="more"><T>More</T></option>
                </select>

                {realEstatePropertyCount === 'more' ? (
                  <>
                    <label htmlFor="real-estate-properties-more"><T>Property Addresses</T></label>
                    <textarea
                      id="real-estate-properties-more"
                      rows={3}
                      maxLength={2000}
                      value={realEstatePropertiesMore}
                      onChange={(event) => setRealEstatePropertiesMore(event.target.value)}
                      placeholder={listAllPropertyAddresses}
                    />
                    <p className="intake-character-count">{realEstatePropertiesMore.length}/2000</p>
                  </>
                ) : (
                  realEstateProperties.map((property, idx) => (
                    <div className="intake-property-block" key={`real-estate-property-${idx}`}>
                      <label htmlFor={`real-estate-property-address-${idx}`}>
                        {propertyAddressLabel} {idx + 1}
                      </label>
                      <input
                        id={`real-estate-property-address-${idx}`}
                        type="text"
                        value={property.address}
                        onChange={(event) => updatePropertyField(
                          idx,
                          'address',
                          event.target.value,
                          setRealEstateProperties,
                        )}
                      />

                      <div className="intake-property-meta-grid">
                        <div>
                          <label htmlFor={`real-estate-year-purchased-${idx}`}><T>Year Purchased</T></label>
                          <select
                            id={`real-estate-year-purchased-${idx}`}
                            value={property.yearPurchased}
                            onChange={(event) => updatePropertyField(
                              idx,
                              'yearPurchased',
                              event.target.value,
                              setRealEstateProperties,
                            )}
                          >
                            {YEAR_OPTIONS.map((year) => (
                              <option key={`real-estate-year-${idx}-${year}`} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`real-estate-current-fmv-${idx}`}><T>Current FMV</T></label>
                          <div className="intake-currency-input">
                            <span>$</span>
                            <input
                              id={`real-estate-current-fmv-${idx}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={property.currentFmv}
                              onChange={(event) => updatePropertyField(
                                idx,
                                'currentFmv',
                                event.target.value,
                                setRealEstateProperties,
                              )}
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor={`real-estate-mortgage-${idx}`}><T>Mortgage</T></label>
                          <div className="intake-currency-input">
                            <span>$</span>
                            <input
                              id={`real-estate-mortgage-${idx}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={property.mortgage}
                              onChange={(event) => updatePropertyField(
                                idx,
                                'mortgage',
                                event.target.value,
                                setRealEstateProperties,
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                <label htmlFor="agent-contact"><T>Agent&apos;s Full Name and Contact</T></label>
                <input
                  id="agent-contact"
                  type="text"
                  value={agentContact}
                  onChange={(event) => setAgentContact(event.target.value)}
                />

                <label htmlFor="closing-date"><T>Date of Closing</T></label>
                <input
                  id="closing-date"
                  type="date"
                  value={closingDate}
                  onChange={(event) => setClosingDate(event.target.value)}
                />
              </div>
            )}

            {hasFamily && (
              <div className="intake-conditional-block">
                <h3><T>Parties Names and Occupations</T></h3>
                {familyParties.map((party, idx) => (
                  <input
                    key={`family-party-${idx}`}
                    type="text"
                    value={party}
                    onChange={(event) => updateFamilyParty(idx, event.target.value)}
                    placeholder={`${partyOccupationPrefix} ${idx + 1}`}
                  />
                ))}
                <button type="button" className="btn btn-secondary" onClick={addFamilyPartyField}>
                  <T>Add Another Entry</T>
                </button>

                <div className="intake-grid two-col">
                  <div>
                    <label htmlFor="family-children-count"><T>Number of Children</T></label>
                    <input
                      id="family-children-count"
                      type="number"
                      min="0"
                      step="1"
                      value={familyChildrenCount}
                      onChange={(event) => {
                        const parsed = Number(event.target.value);
                        if (Number.isNaN(parsed)) {
                          setFamilyChildrenCount(0);
                          return;
                        }
                        setFamilyChildrenCount(Math.max(0, Math.floor(parsed)));
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="family-marriage-year"><T>Marriage Date</T></label>
                    <select
                      id="family-marriage-year"
                      value={familyMarriageYear}
                      onChange={(event) => setFamilyMarriageYear(event.target.value)}
                    >
                      {YEAR_OPTIONS.map((year) => (
                        <option key={`family-marriage-year-${year}`} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="intake-grid two-col">
                  <div>
                    <label htmlFor="family-married-years"><T>Married for How Long</T></label>
                    <select
                      id="family-married-years"
                      value={familyMarriedYears}
                      onChange={(event) => setFamilyMarriedYears(event.target.value)}
                    >
                      {MARRIED_YEAR_OPTIONS.map((yearCount) => (
                        <option key={`family-married-years-${yearCount}`} value={yearCount}>
                          {yearCount} {Number(yearCount) === 1 ? yearLabel : yearsLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="family-property-count"><T>Number of Properties</T></label>
                    <select
                      id="family-property-count"
                      value={familyPropertyCount}
                      onChange={(event) => handlePropertyCountChange(
                        event.target.value,
                        setFamilyPropertyCount,
                        setFamilyProperties,
                      )}
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="more"><T>More</T></option>
                    </select>
                  </div>
                </div>

                {familyPropertyCount === 'more' ? (
                  <>
                    <label htmlFor="family-properties-more"><T>Property Addresses</T></label>
                    <textarea
                      id="family-properties-more"
                      rows={3}
                      maxLength={2000}
                      value={familyPropertiesMore}
                      onChange={(event) => setFamilyPropertiesMore(event.target.value)}
                      placeholder={listAllFamilyPropertyAddresses}
                    />
                    <p className="intake-character-count">{familyPropertiesMore.length}/2000</p>
                  </>
                ) : (
                  familyProperties.map((property, idx) => (
                    <div className="intake-property-block" key={`family-property-${idx}`}>
                      <label htmlFor={`family-property-address-${idx}`}>
                        {propertyAddressLabel} {idx + 1}
                      </label>
                      <input
                        id={`family-property-address-${idx}`}
                        type="text"
                        value={property.address}
                        onChange={(event) => updatePropertyField(
                          idx,
                          'address',
                          event.target.value,
                          setFamilyProperties,
                        )}
                      />

                      <div className="intake-property-meta-grid">
                        <div>
                          <label htmlFor={`family-year-purchased-${idx}`}><T>Year Purchased</T></label>
                          <select
                            id={`family-year-purchased-${idx}`}
                            value={property.yearPurchased}
                            onChange={(event) => updatePropertyField(
                              idx,
                              'yearPurchased',
                              event.target.value,
                              setFamilyProperties,
                            )}
                          >
                            {YEAR_OPTIONS.map((year) => (
                              <option key={`family-year-${idx}-${year}`} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`family-current-fmv-${idx}`}><T>Current FMV</T></label>
                          <div className="intake-currency-input">
                            <span>$</span>
                            <input
                              id={`family-current-fmv-${idx}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={property.currentFmv}
                              onChange={(event) => updatePropertyField(
                                idx,
                                'currentFmv',
                                event.target.value,
                                setFamilyProperties,
                              )}
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor={`family-mortgage-${idx}`}><T>Mortgage</T></label>
                          <div className="intake-currency-input">
                            <span>$</span>
                            <input
                              id={`family-mortgage-${idx}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={property.mortgage}
                              onChange={(event) => updatePropertyField(
                                idx,
                                'mortgage',
                                event.target.value,
                                setFamilyProperties,
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </fieldset>

          {status === 'sent' && (
            <div className="intake-status success" role="status">
              <CheckCircle size={18} />
              <T>Message sent successfully.</T>
            </div>
          )}

          {status === 'error' && (
            <div className="intake-status error" role="alert">
              <AlertCircle size={18} />
              {errorMsg || <T>Unable to send. Please call (905) 604-5521</T>}
            </div>
          )}

          <div className="intake-actions no-print">
            <button type="submit" className="btn btn-primary" disabled={status === 'sending'}>
              <Send size={18} />
              {status === 'sending' ? <T>Sending...</T> : <T>Submit</T>}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handlePrint}>
              <Printer size={18} />
              <T>Print Form</T>
            </button>
          </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default IntakePage;